import { addDays, addMonths, differenceInCalendarDays, format, formatISO, getDay, intlFormat, isSameDay, isSameMonth, isSameYear, isValid, isWithinInterval, parseISO, startOfDay } from 'date-fns';

const intlDateTimeFormatCache = new Map();

/**
 * Reuses Intl.DateTimeFormat instances for the same locale and options (expensive to construct).
 * @param {string} locale
 * @param {Intl.DateTimeFormatOptions} options
 * @returns {Intl.DateTimeFormat} Cached formatter for the given locale and options.
 */
function getCachedIntlDateTimeFormat(locale, options) {
  const key = `${locale}\0${JSON.stringify(options)}`;
  let formatter = intlDateTimeFormatCache.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options);
    intlDateTimeFormatCache.set(key, formatter);
  }
  return formatter;
}

/**
 * Bare "en" maps to en-GB so trip ranges use day-first order (e.g. 22 April – 23 April 2026).
 * @param {string} [locale]
 * @returns {string} BCP 47 locale for Intl.DateTimeFormat
 */
function resolveIntlLocaleForDateRange(locale) {
  if (!locale || typeof locale !== 'string') {
    return 'en-GB';
  }
  const normalized = locale.replace(/_/g, '-');
  const lower = normalized.toLowerCase();
  if (lower === 'en') {
    return 'en-GB';
  }
  if (lower === 'en-us') {
    return 'en-US';
  }
  return normalized;
}

export function useDateTime() {
  const today = startOfDay(Date.now());
  const tomorrow = addDays(today, 1);

  const formatToObject = (dt) => {
    return {
      year: dt.getFullYear(),
      month: dt.getMonth() + 1,
      day: dt.getDate(),
    };
  };

  const formatFromObjectToDate = (dt) => {
    return new Date(dt.year, dt.month - 1, dt.day);
  };

  const formatToFlat = (dt, dtFormat = 'yyyy-MM-dd') => {
    return format(formatFromObjectToDate(dt), dtFormat);
  };

  const formatFromFlatToObject = (dt) => {
    return formatToObject(parseISO(dt));
  };

  const convertToFormat = (dt, format) => {
    switch (format) {
      case 'object':
        return formatToObject(dt);
      case 'flat':
        return formatToFlat(formatToObject(dt));
      default:
        return dt;
    }
  };

  const getLeadTime = (dt) => {
    const arrivalDateISO = parseISO(dt);
    return differenceInCalendarDays(arrivalDateISO, today);
  };

  const getDiffInDays = (startDate, endDate) => {
    startDate = startDate?.year
      ? formatToFlat(startDate)
      : startDate instanceof Date
        ? formatISO(startDate)
        : startDate;
    endDate = endDate?.year ? formatToFlat(endDate) : endDate instanceof Date ? formatISO(endDate) : endDate;
    const startDateObj = parseISO(startDate);
    const endDateObj = parseISO(endDate);
    return differenceInCalendarDays(endDateObj, startDateObj);
  };

  const isDateWithinInterval = (dt, periodStart, periodEnd) => {
    const compareDate = dt || today;
    return isWithinInterval(compareDate, {
      start: periodStart,
      end: periodEnd,
    });
  };

  const addDaysToDate = (dt, numOfDays) => {
    const startDate = dt ? new Date(dt.year, dt.month - 1, dt.day) : today;
    return formatToObject(addDays(startDate, numOfDays));
  };

  const addMonthsToDate = (dt, numOfMonths, returnFormat = 'object') => {
    const startDate = dt ? new Date(dt.year, dt.month - 1, dt.day) : today;
    const targetDate = addMonths(startDate, numOfMonths);
    return convertToFormat(targetDate, returnFormat);
  };

  const formatToNativeDate = (dt) => {
    if (dt instanceof Date) {
      return dt;
    }
    return dt?.year ? formatFromObjectToDate(dt) : parseISO(dt);
  };

  const formatToHumanReadableShortDate = (dt, lang, format = null) => {
    const realDate = formatToNativeDate(dt);

    const localDayString = intlFormat(realDate, { day: 'numeric' }, { locale: lang });
    const localShortMonthString = intlFormat(realDate, { month: 'short' }, { locale: lang });
    const localYearString = intlFormat(realDate, { year: 'numeric' }, { locale: lang });

    switch (format) {
      case 'month|year':
        return `${localShortMonthString} ${localYearString}`;
      case 'month, year':
        return `${localShortMonthString}, ${localYearString}`;
      default:
        return `${localDayString} ${localShortMonthString}`;
    }
  };

  const formatPeriodToHumanReadable = (startDate, endDate, locale) => {
    if (!startDate || !endDate) {
      return null;
    }

    const realStartDate = formatToNativeDate(startDate);
    const realEndDate = formatToNativeDate(endDate);

    if (isSameDay(realStartDate, realEndDate)) {
      // Same day, show only the day and the month and the year
      // Ex: 03 Feb, 2026
      return `${realStartDate.getDate()} ${formatToHumanReadableShortDate(realStartDate, locale, 'month, year')}`;
    } else if (isSameMonth(realStartDate, realEndDate)) {
      // Different days within the same month, show the day period and the month and the year
      // Ex: 13-20 Feb, 2026
      return `${realStartDate.getDate()}-${realEndDate.getDate()} ${formatToHumanReadableShortDate(realStartDate, locale, 'month, year')}`;
    } else if (isSameYear(realStartDate, realEndDate)) {
      // Different months within the same year, show the day and month of both dates and year
      // Ex: 13 Feb - 20 Mar, 2026
      return `${formatToHumanReadableShortDate(realStartDate, locale)} - ${formatToHumanReadableShortDate(realEndDate, locale)}, ${realEndDate.getFullYear()}`;
    } else {
      // Different year, show the day and month of both dates and both years
      // Ex: 13 Feb 2026 - 20 Mar 2027
      return `${formatToHumanReadableShortDate(realStartDate, locale)}, ${realStartDate.getFullYear()} - ${formatToHumanReadableShortDate(realEndDate, locale)}, ${realEndDate.getFullYear()}`;
    }
  };

  const getDayOfWeek = (date) => {
    const dt = date?.year ? formatFromObjectToDate(date) : date;
    return getDay(dt);
  };

  /**
   * Formats an arrival–departure range with locale-correct day/month order (Intl.DateTimeFormat).
   * @param {string|Date} arrivalDate
   * @param {string|Date} departureDate
   * @param {string} [locale]
   * @returns {string} Localised range such as "15 January – 19 January 2025", or empty string if invalid.
   */
  const formatDateRangeForLocale = (arrivalDate, departureDate, locale = 'en-GB') => {
    if (!arrivalDate || !departureDate) {
      return '';
    }

    const arrivalParsed = formatToNativeDate(arrivalDate);
    const departureParsed = formatToNativeDate(departureDate);

    if (Number.isNaN(arrivalParsed.getTime()) || Number.isNaN(departureParsed.getTime())) {
      return '';
    }

    const resolvedLocale = resolveIntlLocaleForDateRange(locale);

    const arrivalYear = arrivalParsed.getFullYear();
    const departureYear = departureParsed.getFullYear();
    const sameYear = arrivalYear === departureYear;

    const fullDateOptions = {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    };

    const rangeStartOptions = sameYear
      ? {
        day: 'numeric',
        month: 'long',
      }
      : fullDateOptions;

    const rangeStart = getCachedIntlDateTimeFormat(resolvedLocale, rangeStartOptions).format(arrivalParsed);
    const rangeEnd = getCachedIntlDateTimeFormat(resolvedLocale, fullDateOptions).format(departureParsed);

    return `${rangeStart} - ${rangeEnd}`;
  };

  /**
   * Trip list / detail: date range plus nights (uses t('t_NIGHTS')).
   * @returns {string} Range plus nights suffix, or empty string if dates invalid.
   */
  const formatTripDateRange = (arrivalDate, departureDate, lengthOfStayInDays, t, locale = 'en-GB') => {
    const range = formatDateRangeForLocale(arrivalDate, departureDate, locale);
    if (!range) {
      return '';
    }
    const nightsCount = Number(lengthOfStayInDays);
    const nightsText = nightsCount === 1 ? t('t_NIGHT') : t('t_NIGHTS');
    return `${range}, ${lengthOfStayInDays} ${nightsText}`;
  };

  /**
   * Long single date for cancellation and similar (Intl, locale-aware punctuation).
   * @returns {string} Localised long date, or empty string if invalid.
   */
  const formatLongDateForLocale = (dateInput, locale = 'en-GB') => {
    if (!dateInput) {
      return '';
    }
    const d = dateInput instanceof Date ? dateInput : formatToNativeDate(dateInput);
    if (Number.isNaN(d.getTime())) {
      return '';
    }
    return getCachedIntlDateTimeFormat(locale, {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }).format(d);
  };

  const isValidDate = (dt) => {
    if (!dt) {
      return false;
    }

    let date;

    if (dt instanceof Date) {
      date = dt;
    } else if (typeof dt === 'string') {
      date = parseISO(dt);
    } else if (typeof dt === 'object' && dt.year) {
      date = new Date(dt.year, dt.month - 1, dt.day);
      // Date constructor silently normalises overflow (e.g. Feb 30 -> Mar 2),
      // so reject inputs whose components don't round-trip.
      if (date.getFullYear() !== dt.year || date.getMonth() !== dt.month - 1 || date.getDate() !== dt.day) {
        return false;
      }
    } else {
      return false;
    }

    return isValid(date) && date.getFullYear() > 1970;
  };

  /**
   * Whether an ISO calendar date string is today.
   * Compares UTC and device-local calendar days so travellers with mismatched device TZ still match.
   * @param {string} isoDateString - YYYY-MM-DD
   * @returns {boolean} True when the date is today in UTC or local calendar.
   */
  const isToday = (isoDateString) => {
    if (!isoDateString) {
      return false;
    }

    const now = new Date();

    return (
      isoDateString === now.toISOString().slice(0, 10) || isoDateString === format(now, 'yyyy-MM-dd')
    );
  };

  return {
    today: formatToObject(today),
    tomorrow: formatToObject(tomorrow),
    addDaysToDate,
    addMonthsToDate,
    getLeadTime,
    getDiffInDays,
    formatToObject,
    formatToFlat,
    formatToHumanReadableShortDate,
    formatPeriodToHumanReadable,
    formatFromFlatToObject,
    formatFromObjectToDate,
    getDayOfWeek,
    isValidDate,
    isDateWithinInterval,
    formatDateRangeForLocale,
    formatTripDateRange,
    formatLongDateForLocale,
    isToday,
  };
}
